/**
 * Universal Actions
 * Actions that are available across all or most entities
 * These are shared definitions to reduce duplication
 */

import type { BaseEntity, EntityWithRelations } from "@/components/shared/UniversalCRMTable/types";
import type { DetailActionStrict } from "../types/detail-action";
import { ROLE_COMBINATIONS } from "../types/action-properties";

/**
 * Universal delete action
 * Available to all entities, admin only
 */
export const universalDeleteAction = <T extends BaseEntity>(
  options: { bulkable?: boolean } = {}
): DetailActionStrict<T> => ({
  id: "delete",
  label: "Delete",
  icon: "trash",
  variant: "destructive",
  action: "deleteItem",
  allowedRoles: [...ROLE_COMBINATIONS.ADMIN_ONLY],
  bulkable: options.bulkable ?? true,
});

/**
 * Universal edit action
 * Available to all entities, employees and admins, detail page only
 */
export const universalEditAction = <T extends BaseEntity>(
  options: { primary?: boolean } = {}
): DetailActionStrict<T> => ({
  id: "edit",
  label: "Edit",
  icon: "edit",
  variant: "default",
  action: "openEditModal",
  allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
  primary: options.primary ?? true,
});

/**
 * Universal send email action
 * Available to all entities, employees and admins, bulkable
 */
export const universalSendEmailAction = <T extends BaseEntity>(
  options: { primary?: boolean; bulkable?: boolean } = {}
): DetailActionStrict<T> => ({
  id: "send-email",
  label: "Send Email",
  icon: "mail",
  variant: "outline",
  action: "sendEmail",
  allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
  primary: options.primary ?? true,
  bulkable: options.bulkable ?? true,
});

/**
 * Universal log activity action
 * Available to all entities, employees and admins
 */
export const universalLogActivityAction = <T extends BaseEntity>(
  options: { primary?: boolean } = {}
): DetailActionStrict<T> => ({
  id: "log-activity",
  label: "Log Activity",
  icon: "activity",
  variant: "outline",
  action: "logActivity",
  allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
  primary: options.primary ?? true,
});

/**
 * Universal ask Meridian action
 * Employees + admins only — client users do not have Meridian access.
 */
export const universalAskMeridianAction = <T extends BaseEntity>(
  options: { primary?: boolean } = {}
): DetailActionStrict<T> => ({
  id: "ask-meridian",
  label: "Ask Meridian",
  icon: "meridian",
  variant: "outline",
  action: "askMeridian",
  tooltip: "Ask Meridian AI assistant about this item",
  allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
  // The "meridian" icon doesn't render at icon size — show the short label
  // text in the row instead so it reads as a tidy word.
  showLabelInRow: true,
  primary: options.primary ?? false,
});

/**
 * Universal make ticket action
 * Available to most entities, employees and admins, bulkable
 */
export const universalMakeTicketAction = <T extends BaseEntity>(
  options: { bulkable?: boolean } = {}
): DetailActionStrict<T> => ({
  id: "make-ticket",
  label: "Make Ticket",
  icon: "ticket",
  variant: "outline",
  action: "makeTicket",
  allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
  bulkable: options.bulkable ?? true,
});

/**
 * Universal log time entry action
 * Available to most entities, employees and admins
 */
export const universalLogTimeEntryAction = <T extends BaseEntity>(): DetailActionStrict<T> => ({
  id: "log-time-entry",
  label: "Log Time Entry",
  icon: "clock",
  variant: "outline",
  action: "logTimeEntry",
  allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
});

/**
 * Universal view company action
 * Available to most entities, all roles
 * Visibility depends on entity having company_id
 */
export const universalViewCompanyAction = <T extends BaseEntity>(): DetailActionStrict<T> => ({
  id: "view-company",
  label: "View Company",
  icon: "building",
  variant: "outline",
  action: "viewCompany",
  allowedRoles: [...ROLE_COMBINATIONS.ALL_AUTHENTICATED],
  visible: (item: T) => {
    // Only show if item has a linked company
    return !!(item as EntityWithRelations<T>).company_id;
  },
});

/**
 * Universal view support inbox action
 *
 * Low-prominence employee triage entry point: opens the company's live Google
 * support-group inbox (the "Support Inbox" tab on the company detail page).
 * Available on company, contact, and ticket — the cross-entity resolver walks
 * to the owning company (see lib/actions/manifests/viewSupportInbox.ts). Kept
 * out of the header primary buttons on purpose (menu/right-click only).
 * Employees + admins only — client users have no support-inbox access.
 */
export const universalViewSupportInboxAction = <T extends BaseEntity>(): DetailActionStrict<T> => ({
  id: "view-support-inbox",
  label: "Support inbox",
  icon: "mail",
  variant: "outline",
  action: "viewSupportInbox",
  allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
  tooltip:
    "Live view of this company's Google support group email — for triage before a ticket exists. Rarely needed.",
});

/**
 * Universal create task action
 * Available to most entities, employees and admins, bulkable
 */
export const universalCreateTaskAction = <T extends BaseEntity>(
  options: { bulkable?: boolean } = {}
): DetailActionStrict<T> => ({
  id: "create-tasks",
  label: "Create Task",
  icon: "plus",
  variant: "outline",
  action: "createTask",
  allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
  bulkable: options.bulkable ?? true,
});

/**
 * Universal schedule meeting action
 * Available to most entities, employees and admins
 */
export const universalScheduleMeetingAction = <T extends BaseEntity>(): DetailActionStrict<T> => ({
  id: "schedule-meeting",
  label: "Schedule Meeting",
  icon: "calendar",
  variant: "outline",
  action: "scheduleMeeting",
  allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
});

/**
 * Universal link company action
 * Available to most entities, employees and admins
 */
export const universalLinkCompanyAction = <T extends BaseEntity>(): DetailActionStrict<T> => ({
  id: "link-company",
  label: "Link Company",
  icon: "link",
  variant: "outline",
  action: "linkCompany",
  allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
});

/**
 * Universal enrich action
 * Available to most entities, employees and admins, bulkable
 */
export const universalEnrichAction = <T extends BaseEntity>(): DetailActionStrict<T> => ({
  id: "enrich-contact",
  label: "Enrich",
  icon: "sparkles",
  variant: "outline",
  action: "enrichContact",
  allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
  tooltip: "Trigger data enrichment from external service",
  bulkable: true,
});

/**
 * Universal view details action (table only)
 * Available to all entities, all roles
 */
export const universalViewDetailsAction = <T extends BaseEntity>(
  actionName: string = "viewDetails"
): DetailActionStrict<T> => ({
  id: "view-details",
  label: "View Details",
  icon: "eye",
  variant: "outline",
  action: actionName as any,
  allowedRoles: [...ROLE_COMBINATIONS.ALL_AUTHENTICATED],
});

/**
 * Universal configure automated tasks action
 * Available to all entities, employees and admins
 * Allows setting up recurring follow-up tasks for any entity
 */
export const universalConfigureAutomatedTasksAction = <T extends BaseEntity>(): DetailActionStrict<T> => ({
  id: "configure-automated-tasks",
  label: "Automated follow-up tasks",
  icon: "repeat",
  variant: "outline",
  action: "configureAutomatedTasks",
  allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
  tooltip: "Schedule recurring follow-up tasks for this record",
});


/**
 * Universal "View history & audit" action — opens the append-only change log
 * for this record (`EntityAuditModal`).
 *
 * Registered on `contacts` and `companies` today (audit wave 1). Staff-only:
 * `allowedRoles` keeps it out of a `client_user`'s menus, and the API route
 * re-checks server-side — a client could not read the log even by calling it
 * directly.
 *
 * Registering it here is the whole entry point: per ENTITY_TABLE_ROW_ACTIONS it
 * then appears in BOTH the `⋯` button and the right-click menu, on every
 * surface that renders the entity.
 */
export const universalViewAuditHistoryAction = <T extends BaseEntity>(): DetailActionStrict<T> => ({
  id: "view-audit-history",
  label: "View history & audit",
  icon: "history",
  variant: "outline",
  action: "viewAuditHistory",
  allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
  tooltip: "Who changed what, when — and restore a specific change",
});
